"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Maximize2, DoorOpen, Layers, ArrowUpToLine, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const planningSteps = [{
  step: "01",
  code: "SPEC-AREA",
  title: "Operating Area",
  description: "Measure the full area available for the bull, inflatable and required surrounding access.",
  icon: Maximize2,
  details: "Perimeter footprint & clearance"
}, {
  step: "02",
  code: "SPEC-ENTRY",
  title: "Entry & Access",
  description: "Check doors, gates, corridors, loading areas and other access points that the packed equipment must pass through.",
  icon: DoorOpen,
  details: "Transit path & pinch points"
}, {
  step: "03",
  code: "SPEC-SURF",
  title: "Surface",
  description: "Review the surface requirements provided for the exact machine and inflatable. Do not assume every system can operate on every surface.",
  icon: Layers,
  details: "Grade, substrate & anchor rating"
}, {
  step: "04",
  code: "SPEC-OVHD",
  title: "Overhead Clearance",
  description: "Where overhead clearance matters, confirm the requirements for the exact equipment and location.",
  icon: ArrowUpToLine,
  details: "Ceilings, trusses & lighting gear"
}];
export default function LocationPlanning() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="locationplanning" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="inline-block size-2 bg-primary" />
            <span className="font-mono text-xs tracking-wider uppercase text-muted-foreground font-semibold">
              SITE SPECIFICATIONS &amp; PRE-DELIVERY
            </span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground leading-[1.05]">
            Plan the Mechanical Bull Location Before Delivery
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
            The location affects almost every other setup decision. Before
            buying, consider where the equipment will operate, how it will reach
            that area and whether the complete system fits once assembled.
          </p>
        </div>

        {/* 4-Column Spec Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-px sm:bg-border border border-border">
          {planningSteps.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.code} data-index={index} variants={cardVariants} className="bg-card p-6 sm:p-7 flex flex-col justify-between border sm:border-0 border-border transition-[background-color,border-color] duration-150 hover:bg-muted/40 group">
                <div>
                  {/* Top Meta Bar */}
                  <div className="flex items-center justify-between pb-4 mb-5 border-b border-border text-muted-foreground">
                    <span className="font-mono text-xs font-semibold tracking-wider text-primary">
                      STEP {item.step}
                    </span>
                    <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground/80">
                      {item.code}
                    </span>
                  </div>

                  {/* Icon & Heading */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="size-9 bg-muted flex items-center justify-center border border-border text-foreground group-hover:border-primary transition-[border-color] duration-150">
                      <Icon className="size-4 text-foreground" />
                    </div>
                    <h3 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-tight text-foreground leading-none">
                      {item.title}
                    </h3>
                  </div>

                  {/* Technical Copy */}
                  <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                    {item.description}
                  </p>
                </div>

                {/* Sub-label technical note */}
                <div className="mt-8 pt-3 border-t border-border/60 flex items-center gap-2">
                  <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                  <span className="font-mono text-xs text-muted-foreground">
                    {item.details}
                  </span>
                </div>
              </motion.div>;
        })}
        </motion.div>

        {/* Industrial Caution Notice Bar */}
        <div className="mt-6 border border-border bg-card p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3.5">
            <div className="size-8 bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
              <AlertTriangle className="size-4 text-primary" />
            </div>
            <div>
              <span className="font-mono text-[11px] uppercase tracking-wider text-primary font-semibold block sm:inline sm:mr-2">
                CRITICAL CHECKPOINT:
              </span>
              <span className="text-sm text-foreground font-medium">
                Measure the location before purchasing rather than trying to make the equipment fit after delivery.
              </span>
            </div>
          </div>

          <div className="shrink-0 w-full sm:w-auto">
            <Button asChild variant="outline" size="sm" className="w-full sm:w-auto rounded-none border-border font-mono text-xs uppercase tracking-wider hover:border-foreground">
              <Link to="/SetupSafetyAndMaintenancePage">
                Setup specs
                <ArrowRight className="size-3 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}